/**
 * Variety tools: auto-cycling visualizations and palettes with the beat, "Surprise me" random
 * scenes, and one-click oscillation of a parameter. Pure logic; the AppController applies the
 * results through the scene store (so every change is undoable and crossfades).
 */
import { hash01, type Modulation } from '../shared/modulation';
import type { ParamDef } from '../shared/params';
import { MAX_LAYERS, uid, type Layer, type Scene } from '../shared/scene';
import { vizRole, type VisualizationDef, type VizRole } from '../shared/viz';

/** Visualizations usable for a role: MilkDrop presets only as backgrounds, and only if allowed. */
export function vizPool(
  all: readonly VisualizationDef[],
  role: VizRole,
  includeMilkdrop: boolean,
): VisualizationDef[] {
  return all.filter((v) =>
    v.kind === 'butterchurn' ? includeMilkdrop && role === 'background' : vizRole(v) === role,
  );
}

/** The next item after `current` (in order) or a random one that isn't current or recent. */
export function pickNext<T extends { id: string }>(
  pool: readonly T[],
  currentId: string | null,
  order: 'shuffle' | 'sequential',
  recent: readonly string[],
  rand: () => number,
): T | null {
  if (pool.length === 0) return null;
  if (order === 'sequential') {
    const i = pool.findIndex((p) => p.id === currentId);
    return pool[(i + 1) % pool.length]!;
  }
  let candidates = pool.filter((p) => p.id !== currentId && !recent.includes(p.id));
  if (candidates.length === 0) candidates = pool.filter((p) => p.id !== currentId);
  if (candidates.length === 0) return pool[0]!;
  return candidates[Math.floor(rand() * candidates.length) % candidates.length]!;
}

/** Deterministic random stream (tests pass a seed; the app passes Date.now()). */
export function rng(seed: number): () => number {
  let n = Math.floor(seed) % 2147483647;
  return () => hash01(n++);
}

/** Parameter values jittered around their defaults; quality knobs and selects stay put. */
export function randomParams(params: readonly ParamDef[], rand: () => number, spread = 0.3) {
  const out: Record<string, number> = {};
  for (const p of params) {
    if (p.quality || p.control === 'select' || p.control === 'toggle') continue;
    const range = p.max - p.min;
    let v = p.default + (rand() * 2 - 1) * spread * range;
    v = Math.min(p.max, Math.max(p.min, v));
    if (p.step) v = Math.round(v / p.step) * p.step;
    out[p.id] = v;
  }
  return out;
}

export interface SurpriseOpts {
  backgrounds: readonly VisualizationDef[];
  elements: readonly VisualizationDef[];
  palettes: readonly { id: string }[];
  /** Chance (0..1) of adding a second, shapes/particles layer. */
  elementChance?: number;
}

/**
 * "Surprise me": a new full-screen visualization, often a shapes/particles layer over it (blend
 * 'over'), a new palette, varied parameters, and sometimes trails or a kaleidoscope. Keeps
 * transition, LFOs and settings.
 */
export function surpriseScene(current: Scene, opts: SurpriseOpts, rand: () => number): Scene {
  const pick = <T>(xs: readonly T[]): T | undefined => xs[Math.floor(rand() * xs.length)];
  const bgDef = pick(opts.backgrounds.filter((v) => v.id !== current.layers[0]?.vizId));
  const bg = bgDef ?? opts.backgrounds[0];
  const layers: Layer[] = [];
  if (bg) {
    layers.push({
      id: uid('l'),
      vizId: bg.id,
      params: randomParams(bg.params, rand),
      blend: 'normal',
      opacity: 1,
      enabled: true,
    });
  }
  const fg = rand() < (opts.elementChance ?? 0.65) ? pick(opts.elements) : undefined;
  if (fg) {
    layers.push({
      id: uid('l'),
      vizId: fg.id,
      params: randomParams(fg.params, rand),
      blend: 'over',
      opacity: 0.9,
      enabled: true,
    });
  }
  if (layers.length === 0) return current;

  const postfx: Scene['postfx'] = {};
  const r = rand();
  if (r < 0.25) {
    postfx.feedback = {
      enabled: 1,
      amount: 0.55 + rand() * 0.3,
      zoom: 1 + (rand() * 2 - 1) * 0.03,
      rotate: (rand() * 2 - 1) * 0.02,
    };
  } else if (r < 0.4) {
    postfx.mirror = { mode: 4, segments: 4 + Math.floor(rand() * 7), spin: (rand() * 2 - 1) * 0.2 };
  } else if (r < 0.5) {
    postfx.mirror = { mode: 1 + Math.floor(rand() * 3) };
  }
  const palette = pick(opts.palettes.filter((p) => p.id !== current.palette.id));

  return {
    ...current,
    name: 'Surprise',
    layers,
    palette: palette ? { id: palette.id } : current.palette,
    paletteShift: 0,
    postfx,
    // Modulations target the old layers' ids; they don't carry over.
    modulations: [],
  };
}

/** What randomizing leaves alone: the overall colours, the overall effects, and some layers. */
export interface Pins {
  palette: boolean;
  effects: boolean;
  /** Layer ids. */
  layers: readonly string[];
}

export const NO_PINS: Pins = { palette: false, effects: false, layers: [] };

/**
 * `next` (a random look) with what's pinned in `current` carried over unchanged: pinned layers
 * keep their place in the stack (taking the slot of whatever `next` had there), and pinned colours
 * or effects stay as they are. Modulations go with what they drive.
 */
export function keepPinned(current: Scene, next: Scene, pins: Pins): Scene {
  const pinned = current.layers
    .map((layer, index) => ({ layer, index }))
    .filter(({ layer }) => pins.layers.includes(layer.id));
  if (!pins.palette && !pins.effects && pinned.length === 0) return next;

  const pinnedIds = new Set(pinned.map(({ layer }) => layer.id));
  const layers = next.layers.filter((l) => !pinnedIds.has(l.id));
  for (const { layer, index } of pinned) {
    if (index < layers.length) layers[index] = layer;
    else layers.push(layer);
  }
  // Over the limit: drop unpinned layers from the top.
  for (let i = layers.length - 1; layers.length > MAX_LAYERS && i >= 0; i--)
    if (!pinnedIds.has(layers[i]!.id)) layers.splice(i, 1);

  const ids = new Set(layers.map((l) => l.id));
  const layerOf = (target: string) => (target.startsWith('layer/') ? target.split('/')[1] : null);
  const isPalette = (t: string) => t === 'palette/shift';
  const isFx = (t: string) => t.startsWith('fx/');
  const fromNext = next.modulations.filter((m) => {
    const id = layerOf(m.target);
    if (id) return ids.has(id) && !pinnedIds.has(id);
    return !(pins.palette && isPalette(m.target)) && !(pins.effects && isFx(m.target));
  });
  const fromCurrent = current.modulations.filter((m) => {
    const id = layerOf(m.target);
    if (id) return pinnedIds.has(id);
    return (pins.palette && isPalette(m.target)) || (pins.effects && isFx(m.target));
  });

  return {
    ...next,
    layers,
    palette: pins.palette ? current.palette : next.palette,
    paletteShift: pins.palette ? current.paletteShift : next.paletteShift,
    postfx: pins.effects ? current.postfx : next.postfx,
    modulations: [...fromNext, ...fromCurrent],
  };
}

/** Speeds offered by the oscillate menu, in beats per cycle. */
export const OSC_SPEEDS = [1, 2, 4, 8, 16, 32] as const;

/** A modulation that swings a parameter both ways around its set value (Modulation.osc). */
export function oscillation(
  target: string,
  beatsPerCycle = 8,
  depth = 0.25,
  shape: 'sine' | 'triangle' | 'saw' | 'square' | 'random' = 'sine',
): Modulation {
  return {
    id: uid('m'),
    target,
    source: 'osc',
    amount: depth,
    curve: 'linear',
    attackMs: shape === 'random' || shape === 'square' ? 150 : 0,
    releaseMs: shape === 'random' || shape === 'square' ? 150 : 0,
    osc: { shape, rate: beatsPerCycle, sync: true },
    bipolar: true,
  };
}
